import React, { useState } from 'react'

interface PromptManagerProps {
  prompts: string[]
  onAdd: (prompt: string) => void
  onUpdate: (id: string, prompt: string) => void
  onDelete: (id: string) => void
  isLoading: boolean
}

const PromptManager: React.FC<PromptManagerProps> = ({
  prompts,
  onAdd,
  onUpdate,
  onDelete,
  isLoading,
}) => {
  const [newPrompt, setNewPrompt] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validatePrompt = (prompt: string): string | null => {
    if (!prompt.trim()) {
      return 'Prompt cannot be empty'
    }
    if (prompt.length < 10) {
      return 'Prompt must be at least 10 characters long'
    }
    if (prompt.length > 1000) {
      return 'Prompt must be less than 1000 characters'
    }
    return null
  }

  const handleAddPrompt = () => {
    const error = validatePrompt(newPrompt)
    if (error) {
      setErrors({ new: error })
      return
    }

    onAdd(newPrompt)
    setNewPrompt('')
    setErrors({})
  }

  const handleStartEdit = (id: string, text: string) => {
    setEditingId(id)
    setEditingText(text)
    setErrors({})
  }

  const handleSaveEdit = () => {
    if (!editingId) return

    const error = validatePrompt(editingText)
    if (error) {
      setErrors({ [editingId]: error })
      return
    }

    onUpdate(editingId, editingText)
    setEditingId(null)
    setEditingText('')
    setErrors({})
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditingText('')
    setErrors({})
  }

  const handleDeletePrompt = (id: string) => {
    if (window.confirm('Are you sure you want to delete this prompt?')) {
      onDelete(id)
    }
  }

  return (
    <div className="space-y-6">
      {/* Add New Prompt */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Add New Alternative Prompt</h4>
        <div className="space-y-3">
          <textarea
            value={newPrompt}
            onChange={(e) => setNewPrompt(e.target.value)}
            placeholder="Enter a new alternative prompt..."
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={isLoading}
          />
          {errors.new && (
            <p className="text-sm text-red-600">{errors.new}</p>
          )}
          <button
            onClick={handleAddPrompt}
            disabled={!newPrompt.trim() || isLoading}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Add Prompt
          </button>
        </div>
      </div>

      {/* Existing Prompts */}
      <div className="space-y-4">
        {prompts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No alternative prompts yet.</p>
            <p className="text-sm">Add your first alternative prompt above.</p>
          </div>
        ) : (
          prompts.map((prompt, index) => {
            const promptId = `prompt-${index}`
            const isEditing = editingId === promptId

            return (
              <div key={promptId} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <h5 className="text-sm font-medium text-gray-900">
                    Alternative Prompt #{index + 1}
                  </h5>
                  <div className="flex space-x-2">
                    {!isEditing && (
                      <>
                        <button
                          onClick={() => handleStartEdit(promptId, prompt)}
                          disabled={isLoading}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeletePrompt(promptId)}
                          disabled={isLoading}
                          className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-3">
                    <textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={isLoading}
                    />
                    {errors[promptId] && (
                      <p className="text-sm text-red-600">{errors[promptId]}</p>
                    )}
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={isLoading}
                        className="bg-primary-600 text-white px-3 py-1 rounded text-sm hover:bg-primary-700 disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={isLoading}
                        className="bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-400 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded p-3">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{prompt}</p>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default PromptManager